import React from "react";
import LegalPageLayout from "../components/LegalPageLayout";
import { LegalSection, LegalP, LegalList } from "../components/LegalContent";
import { useSeo } from "../lib/useSeo";

export default function SmsTermsPage() {
  useSeo({
    title: "Debt Collector Kill Switch SMS & Communication Terms",
    description: "SMS and communication terms for Debt Collector Kill Switch communications and customer notifications.",
    canonical: "https://debtcollectorkillswitch.com/sms-terms",
  });

  return (
    <LegalPageLayout pageTitle="SMS & Communication Terms" effectiveDate="July 1, 2026">
      <LegalP>
        SMS participation is optional. Consent to receive marketing text messages is not a condition of purchasing the Debt Collector Kill Switch. These terms apply only if you provide a U.S. mobile number and affirmatively opt in to SMS communications.
      </LegalP>

      <LegalSection heading="1. Program Operator">
        <LegalP>
          The Debt Collector Kill Switch SMS and communication program is operated and fulfilled by TDCR Consulting, LLC. These SMS &amp; Communication Terms govern text messages sent in connection with DebtCollectorKillSwitch.com, the Product, orders, access, support, and any optional marketing program.
        </LegalP>
      </LegalSection>

      <LegalSection heading="2. U.S.-Only; Age Requirement">
        <LegalP>
          The SMS program is intended only for U.S. residents age 18 or older using a U.S. mobile number. By opting in, you represent that you are the subscriber or customary user of the number provided and are authorized to consent to messages at that number.
        </LegalP>
      </LegalSection>

      <LegalSection heading="3. Consent to Receive Messages">
        <LegalP>
          If you affirmatively opt in, you authorize us and our messaging service providers to send text messages to the mobile number you provide. Messages may be sent using automated technology. Your consent applies only to the categories of messages covered by the consent you provide. Consent to receive marketing text messages is not a condition of purchase. You may purchase the Product without agreeing to marketing SMS.
        </LegalP>
      </LegalSection>

      <LegalSection heading="4. Types of Messages">
        <LegalP>Depending on your interaction and consent, messages may include:</LegalP>
        <LegalList items={[
          "Order, payment, access, or delivery confirmations.",
          "Security or account-related notices.",
          "Customer-support responses and requested follow-up.",
          "Product-use reminders, updates, or educational information.",
          "Optional promotional or marketing messages when you have consented to receive them.",
        ]} />
      </LegalSection>

      <LegalSection heading="5. Message Frequency and Charges">
        <LegalP>
          Message frequency varies based on your activity, requests, account status, support needs, and communication preferences. Message and data rates may apply according to your wireless plan. Your carrier&rsquo;s message and data terms apply.
        </LegalP>
      </LegalSection>

      <LegalSection heading="6. Opt Out — STOP" highlight testId="sms-opt-out-section">
        <LegalP>
          You may revoke SMS consent at any time. Reply STOP to a message to opt out of future messages from that messaging program. We may send one final confirmation text acknowledging your opt-out. You may also request removal by emailing info@debtcollectorkillswitch.com. We will honor legally valid opt-out requests as required by applicable law. If you use more than one phone number, you must opt out separately for each number.
        </LegalP>
      </LegalSection>

      <LegalSection heading="7. Help — HELP" highlight testId="sms-help-section">
        <LegalP>
          For help, reply HELP to a message when that functionality is available, or email info@debtcollectorkillswitch.com. Do not use SMS for urgent legal, court, deadline, or emergency matters.
        </LegalP>
      </LegalSection>

      <LegalSection heading="8. Number Changes and Reassignment">
        <LegalP>
          If you change, deactivate, or transfer your mobile number, you agree to opt out before giving up the number and to update any account information that contains the old number. You are responsible for ensuring that the number you provide remains yours and is accurate.
        </LegalP>
      </LegalSection>

      <LegalSection heading="9. Carrier and Delivery Disclaimer">
        <LegalP>
          Wireless carriers are not responsible for delayed or undelivered messages. Message delivery is subject to carrier availability, network conditions, device settings, filtering, and other factors outside our control. We do not guarantee that every message will be received.
        </LegalP>
      </LegalSection>

      <LegalSection heading="10. Privacy and Mobile Information">
        <LegalP>
          Our collection and use of personal information is governed by the Privacy Policy. We do not sell your mobile number or SMS opt-in consent to third parties for their independent marketing. We may share mobile information with service providers that help us transmit messages, maintain consent and opt-out records, prevent abuse, provide support, or comply with law.
        </LegalP>
      </LegalSection>

      <LegalSection heading="11. No Legal Advice Through SMS">
        <LegalP>
          Text messages are for communications, support, and educational information only. SMS does not create an attorney-client relationship and is not a substitute for legal advice. Do not rely on a text message to calculate or preserve a court, lawsuit, dispute, validation, appeal, or other legal deadline.
        </LegalP>
      </LegalSection>

      <LegalSection heading="12. Changes or Termination of the SMS Program">
        <LegalP>
          We may modify, suspend, or discontinue some or all SMS communications. We may also change the short code, long code, toll-free number, sender ID, or messaging provider used to deliver messages. Changes do not eliminate opt-out rights required by law.
        </LegalP>
      </LegalSection>

      <LegalSection heading="13. Contact">
        <LegalP>
          Questions about SMS communications may be sent to info@debtcollectorkillswitch.com.
        </LegalP>
      </LegalSection>
    </LegalPageLayout>
  );
}
